import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../../lib/api';
import { useCreatorMe } from '../../../lib/auth';
import { useToast } from '../../../lib/toast';
import { formatFollowers } from '../../../lib/format';
import type { PlatformHandle } from '../../../lib/types';
import { Select } from '../../../components/Select';
import styles from './Hub.module.css';

const PH_PLATFORMS = [
  'TikTok',
  'Instagram',
  'YouTube',
  'Facebook',
  'X (Twitter)',
  'LinkedIn',
  'Snapchat',
  'Other',
];

interface EditingHandle {
  id: string | null;
  platformName: string;
  handle: string;
  followerCount: number;
  platformLink: string;
}

export function ProfileView() {
  const { data } = useCreatorMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState<{
    fullName: string;
    email: string;
    mainLink: string;
    contentDescription: string;
    audienceDescription: string;
    extraNotes: string;
    productBeta: boolean;
    productSleep: boolean;
  }>({
    fullName: '',
    email: '',
    mainLink: '',
    contentDescription: '',
    audienceDescription: '',
    extraNotes: '',
    productBeta: false,
    productSleep: false,
  });

  useEffect(() => {
    if (data) {
      const sub = data.submission || ({} as Record<string, unknown>);
      const products = ([] as string[]).concat(
        (sub as { productInterest?: string[] | string }).productInterest ?? [],
      );
      setForm({
        fullName: ((sub as { fullName?: string }).fullName ?? '') || '',
        email: data.email ?? '',
        mainLink: (sub as { mainLink?: string }).mainLink ?? '',
        contentDescription: (sub as { contentDescription?: string }).contentDescription ?? '',
        audienceDescription: (sub as { audienceDescription?: string }).audienceDescription ?? '',
        extraNotes: (sub as { extraNotes?: string }).extraNotes ?? '',
        productBeta: products.some((p) => p && p.toLowerCase().includes('beta')),
        productSleep: products.some((p) => p && p.toLowerCase().includes('sleep')),
      });
    }
  }, [data, edit]);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      try {
        return await api.post<{ submission?: unknown }>('/api/creator/update-profile', body);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          return { submission: null };
        }
        throw e;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator', 'me'] });
      toast('Changes saved');
      setEdit(false);
    },
    onError: () => toast('Could not save'),
  });

  if (!data) return null;
  const sub = data.submission;
  const products = ([] as string[]).concat(
    (sub as { productInterest?: string[] | string } | undefined)?.productInterest ?? [],
  );
  const hasBeta = form.productBeta || products.some((p) => p.toLowerCase().includes('beta'));
  const hasSleep = form.productSleep || products.some((p) => p.toLowerCase().includes('sleep'));

  function commit() {
    const productInterest: string[] = [];
    if (form.productBeta) productInterest.push('Beta Blockers (situational anxiety)');
    if (form.productSleep) productInterest.push('Prescription Sleep Plan');
    save.mutate({
      fullName: form.fullName.trim(),
      mainLink: form.mainLink.trim(),
      contentDescription: form.contentDescription.trim(),
      audienceDescription: form.audienceDescription.trim(),
      extraNotes: form.extraNotes.trim(),
      productInterest,
    });
  }

  return (
    <>
      <div className={styles.pageActionRow}>
        <div>
          <div className={styles.pageTitle}>Profile</div>
          <div className={styles.pageSubtitle}>
            Your partnership information — keep this up to date if anything changes.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {edit ? (
            <>
              <button type="button" className={styles.btnGhost} onClick={() => setEdit(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={commit}
                disabled={save.isPending}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </>
          ) : (
            <button type="button" className={styles.btnGhost} onClick={() => setEdit(true)}>
              Edit
            </button>
          )}
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardSectionTitle}>Personal info</div>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.fgrid}>
            <div className={styles.fitem}>
              <div className={styles.fieldLabel}>Full name</div>
              {edit ? (
                <input
                  className={styles.fieldInput}
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                />
              ) : (
                <div className={`${styles.fieldValue} ${!sub?.fullName ? styles.fieldEmpty : ''}`}>
                  {sub?.fullName || '—'}
                </div>
              )}
            </div>
            <div className={styles.fitem}>
              <div className={styles.fieldLabel}>Email</div>
              <div className={styles.fieldValue}>{data.email}</div>
            </div>
            <div className={`${styles.fitem} ${styles.fitemFull}`}>
              <div className={styles.fieldLabel}>Profile link</div>
              {edit ? (
                <input
                  className={styles.fieldInput}
                  value={form.mainLink}
                  onChange={(e) => setForm({ ...form, mainLink: e.target.value })}
                  placeholder="https://"
                />
              ) : (sub as { mainLink?: string } | undefined)?.mainLink ? (
                <div className={styles.fieldValue}>
                  <a
                    href={(sub as { mainLink: string }).mainLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {(sub as { mainLink: string }).mainLink}
                  </a>
                </div>
              ) : (
                <div className={`${styles.fieldValue} ${styles.fieldEmpty}`}>—</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PlatformHandlesCard handles={data.platformHandles} />

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardSectionTitle}>Your content</div>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.fgrid}>
            <div className={`${styles.fitem} ${styles.fitemFull}`}>
              <div className={styles.fieldLabel}>How would you describe your content?</div>
              {edit ? (
                <textarea
                  className={styles.fieldTextarea}
                  value={form.contentDescription}
                  onChange={(e) => setForm({ ...form, contentDescription: e.target.value })}
                />
              ) : (
                <div className={`${styles.fieldValue} ${!form.contentDescription ? styles.fieldEmpty : ''}`}>
                  {form.contentDescription || '—'}
                </div>
              )}
            </div>
            <div className={`${styles.fitem} ${styles.fitemFull}`}>
              <div className={styles.fieldLabel}>How would you describe your audience?</div>
              {edit ? (
                <textarea
                  className={styles.fieldTextarea}
                  value={form.audienceDescription}
                  onChange={(e) => setForm({ ...form, audienceDescription: e.target.value })}
                />
              ) : (
                <div className={`${styles.fieldValue} ${!form.audienceDescription ? styles.fieldEmpty : ''}`}>
                  {form.audienceDescription || '—'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardSectionTitle}>Product interest</div>
        </div>
        <div className={styles.cardBody}>
          {edit ? (
            <div className={styles.productList}>
              <label className={`${styles.productItem} ${form.productBeta ? styles.checked : ''}`}>
                <input
                  type="checkbox"
                  checked={form.productBeta}
                  onChange={(e) => setForm({ ...form, productBeta: e.target.checked })}
                />
                <span>
                  Beta Blockers <span style={{ color: '#9ca3af', fontWeight: 400 }}>(situational anxiety)</span>
                </span>
              </label>
              <label className={`${styles.productItem} ${form.productSleep ? styles.checked : ''}`}>
                <input
                  type="checkbox"
                  checked={form.productSleep}
                  onChange={(e) => setForm({ ...form, productSleep: e.target.checked })}
                />
                <span>Prescription Sleep Plan</span>
              </label>
            </div>
          ) : (
            <div>
              {hasBeta && <span className={styles.productTag}>Beta Blockers</span>}
              {hasSleep && <span className={styles.productTag}>Prescription Sleep Plan</span>}
              {!hasBeta && !hasSleep && <div className={`${styles.fieldValue} ${styles.fieldEmpty}`}>—</div>}
            </div>
          )}
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardSectionTitle}>Additional notes</div>
        </div>
        <div className={styles.cardBody}>
          {edit ? (
            <textarea
              className={styles.fieldTextarea}
              value={form.extraNotes}
              onChange={(e) => setForm({ ...form, extraNotes: e.target.value })}
              placeholder="Any questions or anything else you'd like us to know…"
            />
          ) : (
            <div className={`${styles.fieldValue} ${!form.extraNotes ? styles.fieldEmpty : ''}`}>
              {form.extraNotes || 'Nothing added'}
            </div>
          )}
        </div>
      </div>

      <PasswordCard />
    </>
  );
}

function PasswordCard() {
  const toast = useToast();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);

  // We don't know from /api/creator/me whether a password exists (the hash is omitted).
  // First save assumes "no password yet"; the backend allows that. After a successful save
  // we flip hasPassword true so future changes require the current one.

  const save = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post('/api/creator/set-password', body),
    onSuccess: () => {
      toast('Password updated');
      setCurrent('');
      setNewPassword('');
      setConfirm('');
      setHasPassword(true);
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : 'Could not update password');
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (newPassword.length < 8) {
      setErr('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setErr('New password and confirmation do not match.');
      return;
    }
    save.mutate({ currentPassword, newPassword });
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <div className={styles.cardSectionTitle}>Password</div>
      </div>
      <div className={styles.cardBody}>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
          Set a password to sign in directly without waiting for an email link.
          {hasPassword === false && ' Leave "Current password" blank if you have not set one yet.'}
        </p>
        <form onSubmit={submit}>
          {err && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: '10px 14px',
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {err}
            </div>
          )}
          <div className={styles.fgrid}>
            <div className={`${styles.fitem} ${styles.fitemFull}`}>
              <div className={styles.fieldLabel}>Current password</div>
              <input
                type="password"
                className={styles.fieldInput}
                value={currentPassword}
                autoComplete="current-password"
                onChange={(e) => setCurrent(e.target.value)}
                placeholder="(blank if you don't have one yet)"
              />
            </div>
            <div className={styles.fitem}>
              <div className={styles.fieldLabel}>New password</div>
              <input
                type="password"
                className={styles.fieldInput}
                value={newPassword}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
            </div>
            <div className={styles.fitem}>
              <div className={styles.fieldLabel}>Confirm new password</div>
              <input
                type="password"
                className={styles.fieldInput}
                value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <button type="submit" className={styles.btnPrimary} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlatformHandlesCard({ handles }: { handles: PlatformHandle[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<EditingHandle | null>(null);

  const save = useMutation({
    mutationFn: async (h: EditingHandle) => {
      const body = {
        platformName: h.platformName,
        handle: h.handle,
        followerCount: h.followerCount,
        platformLink: h.platformLink,
      };
      if (h.id) return api.patch<PlatformHandle>(`/api/creator/handles/${h.id}`, body);
      return api.post<PlatformHandle>('/api/creator/handles/', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator', 'me'] });
      toast('Saved');
      setEditing(null);
    },
    onError: () => toast('Could not save — please try again'),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/creator/handles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator', 'me'] });
      toast('Removed');
    },
    onError: () => toast('Could not remove — please try again'),
  });

  function blank(): EditingHandle {
    return { id: null, platformName: '', handle: '', followerCount: 0, platformLink: '' };
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          <div className={styles.cardSectionTitle}>Your platforms</div>
          <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 2 }}>
            Keep this list up to date — admins use it when matching you to campaigns.
          </div>
        </div>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.phList}>
          {handles.length === 0 ? (
            <div className={`${styles.fieldValue} ${styles.fieldEmpty}`}>
              No platforms yet — add one below.
            </div>
          ) : (
            handles.map((h) =>
              editing && editing.id === h.id ? (
                <EditForm
                  key={h.id}
                  initial={editing}
                  onChange={setEditing}
                  onCancel={() => setEditing(null)}
                  onSave={() => editing && save.mutate(editing)}
                  saving={save.isPending}
                />
              ) : (
                <div key={h.id} className={styles.phCard}>
                  <span className={styles.phPlatformPill}>{h.platformName}</span>
                  <span className={styles.phHandleText}>{h.handle}</span>
                  <span className={styles.phFollowerText}>
                    {formatFollowers(h.followerCount)} followers
                  </span>
                  {h.platformLink ? (
                    <a
                      className={styles.phLinkText}
                      href={h.platformLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {h.platformLink}
                    </a>
                  ) : (
                    <span className={`${styles.phLinkText} ${styles.phLinkEmpty}`}>—</span>
                  )}
                  <div className={styles.phActions}>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => setEditing({ ...h })}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      style={{ color: '#c92a2a' }}
                      onClick={() => {
                        if (confirm('Remove this platform?')) del.mutate(h.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ),
            )
          )}
          {editing && editing.id === null && (
            <EditForm
              initial={editing}
              onChange={setEditing}
              onCancel={() => setEditing(null)}
              onSave={() => save.mutate(editing)}
              saving={save.isPending}
            />
          )}
        </div>
        <div className={styles.phAddWrap}>
          <button type="button" className={styles.btnGhost} onClick={() => setEditing(blank())}>
            + Add a platform
          </button>
        </div>
      </div>
    </div>
  );
}

function EditForm({
  initial,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  initial: EditingHandle;
  onChange: (h: EditingHandle) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const knownPlatforms = PH_PLATFORMS.slice(0, -1);
  const isOther = initial.platformName && !knownPlatforms.includes(initial.platformName);
  const [selected, setSelected] = useState(isOther ? 'Other' : initial.platformName);
  const [other, setOther] = useState(isOther ? initial.platformName : '');

  function setPlatformValue(sel: string, otherVal: string) {
    setSelected(sel);
    setOther(otherVal);
    onChange({ ...initial, platformName: sel === 'Other' ? otherVal : sel });
  }

  return (
    <div className={styles.phEditForm}>
      <div className={styles.phEditGrid}>
        <Select
          value={selected}
          onChange={(e) => setPlatformValue(e.target.value, other)}
          placeholder="Platform…"
        >
          {PH_PLATFORMS.map((p) => (
            <option key={p}>{p}</option>
          ))}
        </Select>
        {selected === 'Other' && (
          <input
            className={styles.fieldInput}
            placeholder="Platform name"
            value={other}
            onChange={(e) => setPlatformValue('Other', e.target.value)}
          />
        )}
        <input
          className={styles.fieldInput}
          placeholder="@handle"
          value={initial.handle}
          onChange={(e) => onChange({ ...initial, handle: e.target.value })}
        />
        <input
          className={styles.fieldInput}
          type="number"
          placeholder="Followers"
          value={initial.followerCount}
          onChange={(e) =>
            onChange({ ...initial, followerCount: parseInt(e.target.value, 10) || 0 })
          }
        />
        <input
          className={styles.fieldInput}
          type="url"
          placeholder="https://…"
          value={initial.platformLink}
          onChange={(e) => onChange({ ...initial, platformLink: e.target.value })}
        />
      </div>
      <div className={styles.phEditActions}>
        <button type="button" className={styles.btnGhost} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className={styles.btnPrimary} onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
